import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Trash2 } from 'lucide-react';
import ConfirmationModal from './ConfirmationModal';
import api from '../services/api';

const AdminCatering = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, id: null });

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            const response = await api.get('/catering');
            setOrders(response.data);
            setLoading(false);
        } catch (err) {
            setError(err.response?.data?.message || err.message);
            setLoading(false);
        }
    };

    const handleStatusUpdate = async (id, newStatus) => {
        try {
            await api.put(`/catering/${id}`, { status: newStatus });
            
            setOrders(orders.map(order => 
                order.id === id ? { ...order, status: newStatus } : order
            ));
            toast.success("Status updated");
        } catch (err) {
            toast.error('Error updating status: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleDeleteClick = (id) => {
        setDeleteModal({ isOpen: true, id });
    };

    const confirmDelete = async () => {
        const id = deleteModal.id;
        try {
            await api.delete(`/catering/${id}`);
            
            setOrders(orders.filter(order => order.id !== id));
            setDeleteModal({ isOpen: false, id: null });
            toast.success("Order deleted");
        } catch (err) {
            toast.error('Error deleting order: ' + (err.response?.data?.message || err.message));
        }
    };

    const toggleExpand = (id) => {
        setExpandedId(expandedId === id ? null : id);
    };

    if (loading) return <div className="p-8 text-center text-raya-text-tertiary">Loading catering orders...</div>;
    if (error) return <div className="p-8 text-center text-raya-text-tertiary">Error: {error}</div>;

    return (
        <div className="bg-raya-card rounded-lg shadow-sm overflow-hidden border border-raya-border">
            <div className="px-6 py-4 border-b border-raya-border bg-raya-bg-off">
                <h2 className="text-xl font-bold text-raya-text-primary">Catering Orders</h2>
            </div>
            
            {orders.length === 0 ? (
                <div className="p-8 text-center text-raya-text-tertiary">
                    No catering orders found.
                </div>
            ) : (
                <div className="divide-y divide-raya-border">
                    {orders.map((order) => (
                        <div key={order.id} className="p-6 transition hover:bg-raya-bg-off">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3">
                                        <h3 className="text-lg font-medium text-raya-text-primary">
                                            {order.event_type} - {new Date(order.event_date).toLocaleDateString()}
                                        </h3>
                                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border transition ${
                                            order.status === 'confirmed' ? 'bg-raya-accent text-black border-raya-accent' :
                                            order.status === 'completed' ? 'bg-raya-bg-off text-raya-text-secondary border-raya-border' :
                                            'bg-raya-accent/10 text-raya-accent border-raya-accent/20'
                                        }`}>
                                            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                        </span>
                                    </div>
                                    <p className="text-sm text-raya-text-secondary mt-1">
                                        {order.name} • {order.guest_count} Guests
                                    </p>
                                </div>
                                
                                <div className="flex items-center gap-3">
                                    <select
                                        value={order.status}
                                        onChange={(e) => handleStatusUpdate(order.id, e.target.value)}
                                        className="text-sm border-raya-border rounded-md shadow-sm focus:ring-raya-accent focus:border-raya-accent bg-raya-card text-raya-text-primary transition"
                                    >
                                        <option value="pending">Pending</option>
                                        <option value="contacted">Contacted</option>
                                        <option value="confirmed">Confirmed</option>
                                        <option value="completed">Completed</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                    
                                    <button 
                                        onClick={() => toggleExpand(order.id)}
                                        className="px-3 py-1 text-sm font-bold text-raya-accent hover:text-raya-accent-hover transition border border-raya-accent/30 rounded-md hover:bg-raya-accent/10"
                                    >
                                        {expandedId === order.id ? 'Hide Details' : 'View Details'}
                                    </button>
                                    
                                    <button 
                                        onClick={() => handleDeleteClick(order.id)}
                                        className="p-2 text-raya-text-tertiary hover:text-raya-accent transition"
                                        title="Delete Order"
                                    >
                                        <Trash2 size={20} />
                                    </button>
                                </div>
                            </div>
                            
                            {expandedId === order.id && (
                                <div className="mt-4 pt-4 border-t border-raya-border grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                                    <div>
                                        <h4 className="font-medium text-raya-text-primary mb-2">Contact Details</h4>
                                        <p className="text-raya-text-secondary">Email: {order.email}</p>
                                        <p className="text-raya-text-secondary">Phone: {order.phone}</p>
                                        <p className="text-raya-text-secondary">Submitted: {new Date(order.created_at).toLocaleString()}</p>
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-raya-text-primary mb-2">Order Details</h4>
                                        <div className="text-raya-text-secondary">
                                            <p className="mb-1"><strong>Selected Food:</strong></p>
                                            <ul className="list-disc pl-5 mb-2">
                                                {(() => {
                                                    let options = [];
                                                    try {
                                                        if (Array.isArray(order.food_options)) {
                                                            options = order.food_options;
                                                        } else if (typeof order.food_options === 'string') {
                                                            options = JSON.parse(order.food_options);
                                                        }
                                                    } catch (e) {
                                                        console.error("Error parsing food options", e);
                                                        options = ["Error parsing food options"];
                                                    }
                                                    return options.map((item, idx) => (
                                                        <li key={idx}>{item}</li>
                                                    ));
                                                })()}
                                            </ul>
                                            <p><strong>Special Requests:</strong> {order.special_requests || 'None'}</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            <ConfirmationModal 
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, id: null })}
                onConfirm={confirmDelete}
                title="Delete Catering Order"
                message="Are you sure you want to delete this catering order? This action cannot be undone."
            />
        </div>
    );
};

export default AdminCatering;
